import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function PendingKycAccounts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [filter, setFilter] = useState({ search: "", branch: "" });
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    loadPendingAccounts();
    loadBranches();
  }, [pagination.page]);

  const loadPendingAccounts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/BankAccount/pending-kyc?page=${pagination.page}&pageSize=${pagination.pageSize}`);
      setAccounts(res.data.accounts || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.total,
        totalPages: res.data.totalPages
      }));
    } catch (err) {
      console.error("Failed to load pending accounts:", err);
      alert("Failed to load pending KYC accounts");
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await api.get("/Branch");
      setBranches(res.data || []);
    } catch (err) {
      console.error("Failed to load branches:", err);
    }
  };

  const viewAccountDetails = async (account) => {
    setSelectedAccount(account);
    setShowModal(true);
    
    // Load KYC documents for this customer
    try {
      const res = await api.get(`/KYC/documents/${account.id}`);
      setKycDocuments(res.data || []);
    } catch (err) {
      console.error("Failed to load KYC documents:", err);
      // Try alternative endpoint
      try {
        const kycRes = await api.get(`/KYC/user/${account.userId || account.id}`);
        setKycDocuments(kycRes.data?.documents || []);
      } catch (e) {
        setKycDocuments([]);
      }
    }
  };

  const handleApprove = async () => {
    if (!selectedAccount) return;
    
    if (!window.confirm(`Are you sure you want to APPROVE KYC for account ${selectedAccount.accountNumber}?\n\nThis will activate the account and allow the customer to make withdrawals.`)) {
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/BankAccount/approve-kyc/${selectedAccount.id}`, {
        approved: true
      });
      
      alert(`✅ KYC Approved!\n\nAccount ${selectedAccount.accountNumber} has been activated.`);
      setShowModal(false);
      setSelectedAccount(null);
      loadPendingAccounts();
    } catch (err) {
      console.error("Failed to approve KYC:", err);
      alert(err.response?.data?.message || "Failed to approve KYC");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAccount) return;
    
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/BankAccount/approve-kyc/${selectedAccount.id}`, {
        approved: false,
        reason: rejectReason
      });
      
      alert(`❌ KYC Rejected\n\nAccount ${selectedAccount.accountNumber} has been marked as KYC Rejected.`);
      setShowModal(false);
      setShowRejectModal(false);
      setSelectedAccount(null);
      setRejectReason("");
      loadPendingAccounts();
    } catch (err) {
      console.error("Failed to reject KYC:", err);
      alert(err.response?.data?.message || "Failed to reject KYC");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = !filter.search || 
      account.accountNumber?.toLowerCase().includes(filter.search.toLowerCase()) ||
      account.accountName?.toLowerCase().includes(filter.search.toLowerCase()) ||
      account.customerEmail?.toLowerCase().includes(filter.search.toLowerCase()) ||
      account.customerPhone?.includes(filter.search);
    
    const matchesBranch = !filter.branch || account.branch === filter.branch;
    
    return matchesSearch && matchesBranch;
  });

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <div>
          <h1 style={styles.title}>📋 Pending KYC Accounts</h1>
          <p style={styles.subtitle}>Review and approve accounts opened by agents</p>
        </div>
        <div style={styles.statsBox}>
          <div style={styles.statItem}>
            <span style={styles.statNumber}>{pagination.total}</span>
            <span style={styles.statLabel}>Pending</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by account number, name, email, or phone..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            style={styles.searchInput}
          />
        </div>
        <select
          value={filter.branch}
          onChange={(e) => setFilter({ ...filter, branch: e.target.value })}
          style={styles.filterSelect}
        >
          <option value="">All Branches</option>
          {branches.map(b => (
            <option key={b.id} value={b.name}>{b.name}</option>
          ))}
        </select>
        <button onClick={loadPendingAccounts} style={styles.refreshBtn}>
          🔄 Refresh
        </button>
      </div>

      {/* Accounts Table */}
      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading pending accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>✅</span>
            <h3>No Pending KYC Accounts</h3>
            <p>All agent-opened accounts have been reviewed.</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Account Details</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.th}>Opened</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map(account => (
                <tr key={account.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.accountInfo}>
                      <span style={styles.accountNumber}>{account.accountNumber}</span>
                      <span style={styles.accountType}>{account.accountType}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.customerInfo}>
                      <span style={styles.customerName}>{account.accountName}</span>
                      <span style={styles.customerContact}>
                        {account.customerEmail}
                        {account.customerPhone && ` • ${account.customerPhone}`}
                      </span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.branchBadge}>{account.branch}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.balance}>{formatCurrency(account.balance)}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.dateText}>{formatDate(account.openedAt)}</span>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => viewAccountDetails(account)}
                      style={styles.reviewBtn}
                    >
                      👁️ Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            disabled={pagination.page === 1}
            style={styles.pageBtn}
          >
            ← Previous
          </button>
          <span style={styles.pageInfo}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            disabled={pagination.page >= pagination.totalPages}
            style={styles.pageBtn}
          >
            Next →
          </button>
        </div>
      )}

      {/* Review Modal */}
      {showModal && selectedAccount && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>📋 KYC Review</h2>
              <button onClick={() => setShowModal(false)} style={styles.closeBtn}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {/* Account Info */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Account Information</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Account Number</span>
                    <span style={styles.infoValue}>{selectedAccount.accountNumber}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Account Type</span>
                    <span style={styles.infoValue}>{selectedAccount.accountType}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Branch</span>
                    <span style={styles.infoValue}>{selectedAccount.branch}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Initial Balance</span>
                    <span style={styles.infoValue}>{formatCurrency(selectedAccount.balance)}</span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Customer Information</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Full Name</span>
                    <span style={styles.infoValue}>{selectedAccount.accountName}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Email</span>
                    <span style={styles.infoValue}>{selectedAccount.customerEmail}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Phone</span>
                    <span style={styles.infoValue}>{selectedAccount.customerPhone || "N/A"}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Opened Date</span>
                    <span style={styles.infoValue}>{formatDate(selectedAccount.openedAt)}</span>
                  </div>
                </div>
              </div>

              {/* KYC Documents */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>KYC Documents</h3>
                {kycDocuments.length > 0 ? (
                  <div style={styles.documentsGrid}>
                    {kycDocuments.map((doc, index) => (
                      <div key={index} style={styles.documentCard}>
                        <div style={styles.documentHeader}>
                          <span style={styles.documentIcon}>
                            {doc.documentType === "passport" ? "📷" :
                             doc.documentType === "idCard" ? "🪪" :
                             doc.documentType === "utilityBill" ? "📄" :
                             doc.documentType === "signature" ? "✍️" : "📎"}
                          </span>
                          <span style={styles.documentType}>
                            {doc.documentType === "passport" ? "Passport Photo" :
                             doc.documentType === "idCard" ? "ID Card" :
                             doc.documentType === "utilityBill" ? "Utility Bill" :
                             doc.documentType === "signature" ? "Signature" :
                             doc.documentType}
                          </span>
                        </div>
                        {doc.documentUrl ? (
                          <div style={styles.documentPreview}>
                            {doc.documentUrl.toLowerCase().endsWith(".pdf") ? (
                              <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" style={styles.pdfLink}>
                                📄 View PDF
                              </a>
                            ) : (
                              <img 
                                src={doc.documentUrl} 
                                alt={doc.documentType}
                                style={styles.documentImage}
                                onClick={() => window.open(doc.documentUrl, "_blank")}
                              />
                            )}
                          </div>
                        ) : (
                          <div style={styles.noDocument}>No document uploaded</div>
                        )}
                        <div style={styles.documentMeta}>
                          <span>Uploaded: {formatDate(doc.uploadedAt || doc.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.noDocuments}>
                    <span style={styles.warningIcon}>⚠️</span>
                    <p>No KYC documents found for this account.</p>
                    <p style={styles.smallText}>The agent may not have uploaded the required documents.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                style={styles.rejectBtn}
              >
                ❌ Reject KYC
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                style={styles.approveBtn}
              >
                {actionLoading ? "Processing..." : "✅ Approve KYC"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {showRejectModal && (
        <div style={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
          <div style={{ ...styles.modal, maxWidth: "500px" }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>❌ Reject KYC</h2>
              <button onClick={() => setShowRejectModal(false)} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ marginBottom: "15px", color: "#6b7280" }}>
                Please provide a reason for rejecting this KYC application. This will be recorded and may be communicated to the customer.
              </p>
              <label style={styles.label}>Rejection Reason *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                style={styles.textarea}
                rows={4}
              />
              <div style={styles.rejectReasons}>
                <p style={styles.smallLabel}>Common reasons:</p>
                <div style={styles.reasonChips}>
                  {[
                    "ID document is unclear or expired",
                    "Photo does not match ID",
                    "Address proof is invalid",
                    "Missing required documents",
                    "Suspicious information provided"
                  ].map((reason, i) => (
                    <button
                      key={i}
                      onClick={() => setRejectReason(reason)}
                      style={styles.reasonChip}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                style={styles.confirmRejectBtn}
              >
                {actionLoading ? "Processing..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  header: {
    background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
    color: "white",
    padding: "20px 30px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap"
  },
  backBtn: {
    padding: "10px 20px",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500"
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0
  },
  subtitle: {
    fontSize: "14px",
    opacity: 0.8,
    margin: "5px 0 0"
  },
  statsBox: {
    marginLeft: "auto",
    display: "flex",
    gap: "20px"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.15)",
    padding: "10px 20px",
    borderRadius: "10px"
  },
  statNumber: {
    fontSize: "28px",
    fontWeight: "700"
  },
  statLabel: {
    fontSize: "12px",
    opacity: 0.8
  },
  filters: {
    display: "flex",
    gap: "15px",
    padding: "20px 30px",
    backgroundColor: "white",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap"
  },
  searchBox: {
    flex: 1,
    minWidth: "250px",
    position: "relative"
  },
  searchIcon: {
    position: "absolute",
    left: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px"
  },
  searchInput: {
    width: "100%",
    padding: "12px 15px 12px 45px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  filterSelect: {
    padding: "12px 20px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    backgroundColor: "white",
    cursor: "pointer",
    minWidth: "150px"
  },
  refreshBtn: {
    padding: "12px 20px",
    backgroundColor: "#1e3a5f",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  tableContainer: {
    padding: "20px 30px"
  },
  loadingState: {
    textAlign: "center",
    padding: "60px",
    color: "#6b7280"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #1e3a5f",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 15px"
  },
  emptyState: {
    textAlign: "center",
    padding: "60px",
    backgroundColor: "white",
    borderRadius: "12px",
    color: "#6b7280"
  },
  emptyIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "15px"
  },
  table: {
    width: "100%",
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    borderCollapse: "collapse"
  },
  th: {
    padding: "15px 20px",
    textAlign: "left",
    backgroundColor: "#f8fafc",
    fontWeight: "600",
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #e2e8f0"
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background-color 0.2s"
  },
  td: {
    padding: "15px 20px",
    fontSize: "14px",
    color: "#374151"
  },
  accountInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  accountNumber: {
    fontWeight: "600",
    fontFamily: "monospace",
    fontSize: "15px",
    color: "#1e3a5f"
  },
  accountType: {
    fontSize: "12px",
    color: "#6b7280"
  },
  customerInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  customerName: {
    fontWeight: "600"
  },
  customerContact: {
    fontSize: "12px",
    color: "#6b7280"
  },
  branchBadge: {
    padding: "4px 10px",
    backgroundColor: "#e0f2fe",
    color: "#0369a1",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500"
  },
  balance: {
    fontWeight: "600",
    color: "#059669"
  },
  dateText: {
    fontSize: "13px",
    color: "#6b7280"
  },
  reviewBtn: {
    padding: "8px 16px",
    backgroundColor: "#f59e0b",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer"
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
    padding: "20px"
  },
  pageBtn: {
    padding: "10px 20px",
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  pageInfo: {
    fontSize: "14px",
    color: "#6b7280"
  },
  // Modal styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px"
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    maxWidth: "900px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 25px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc"
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: 0,
    color: "#1e3a5f"
  },
  closeBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "#f1f5f9",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  modalBody: {
    padding: "25px",
    overflowY: "auto",
    flex: 1
  },
  infoSection: {
    marginBottom: "25px"
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "1px solid #e2e8f0"
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px"
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  infoLabel: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "500"
  },
  infoValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1f2937"
  },
  documentsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px"
  },
  documentCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    overflow: "hidden",
    backgroundColor: "#fafafa"
  },
  documentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 15px",
    backgroundColor: "#f1f5f9",
    borderBottom: "1px solid #e2e8f0"
  },
  documentIcon: {
    fontSize: "18px"
  },
  documentType: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151"
  },
  documentPreview: {
    height: "150px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white"
  },
  documentImage: {
    maxWidth: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    cursor: "pointer"
  },
  pdfLink: {
    padding: "15px 25px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    borderRadius: "8px",
    textDecoration: "none",
    fontWeight: "600"
  },
  noDocument: {
    padding: "30px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: "13px"
  },
  documentMeta: {
    padding: "10px 15px",
    fontSize: "11px",
    color: "#6b7280",
    borderTop: "1px solid #e2e8f0"
  },
  noDocuments: {
    textAlign: "center",
    padding: "30px",
    backgroundColor: "#fef3c7",
    borderRadius: "10px",
    color: "#92400e"
  },
  warningIcon: {
    fontSize: "32px",
    display: "block",
    marginBottom: "10px"
  },
  smallText: {
    fontSize: "13px",
    opacity: 0.8
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "15px",
    padding: "20px 25px",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc"
  },
  rejectBtn: {
    padding: "12px 25px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  approveBtn: {
    padding: "12px 25px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "8px"
  },
  textarea: {
    width: "100%",
    padding: "12px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    resize: "vertical",
    boxSizing: "border-box"
  },
  rejectReasons: {
    marginTop: "15px"
  },
  smallLabel: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "8px"
  },
  reasonChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px"
  },
  reasonChip: {
    padding: "6px 12px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  cancelBtn: {
    padding: "12px 25px",
    backgroundColor: "#f1f5f9",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  confirmRejectBtn: {
    padding: "12px 25px",
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  }
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
